import Vue from 'vue';
import SurveysAPI from '../../api/surveys';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  // A carteira é paginada e pertence a um medidor por vez — não faz sentido guardar a de
  // todos os medidores em memória.
  wallet: {
    records: [],
    meta: { currentPage: 1, totalCount: 0 },
    surveyGaugeId: null,
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingWallet: false,
    isUpdatingWallet: false,
  },
};

export const getters = {
  getSurveys: $state => $state.records,
  getCsatSurveys: $state =>
    $state.records.filter(record => record.kind === 'csat'),
  getNpsSurveys: $state =>
    $state.records.filter(record => record.kind === 'nps'),
  getSurvey: $state => id =>
    $state.records.find(record => record.id === Number(id)),
  getWallet: $state => $state.wallet,
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  get: async ({ commit }, params = {}) => {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await SurveysAPI.get(params);
      commit('SET_SURVEYS', data.payload || []);
      return data.payload || [];
    } catch (error) {
      throwErrorMessage(error);
      return [];
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  show: async ({ commit }, id) => {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await SurveysAPI.show(id);
      commit('UPSERT_SURVEY', data.payload);
      return data.payload;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  create: async ({ commit }, payload) => {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await SurveysAPI.create({ survey_gauge: payload });
      commit('UPSERT_SURVEY', data.payload);
      return data.payload;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...payload }) => {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await SurveysAPI.update(id, { survey_gauge: payload });
      commit('UPSERT_SURVEY', data.payload);
      return data.payload;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await SurveysAPI.delete(id);
      commit('DELETE_SURVEY', id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  fetchWallet: async ({ commit }, { id, page = 1 }) => {
    commit('SET_UI_FLAG', { isFetchingWallet: true });
    try {
      const { data } = await SurveysAPI.getWallet(id, { page });
      commit('SET_WALLET', {
        records: data.payload || [],
        meta: {
          currentPage: data.meta?.current_page || page,
          totalCount: data.meta?.total_count || 0,
        },
        surveyGaugeId: Number(id),
      });
      return data.payload || [];
    } catch (error) {
      throwErrorMessage(error);
      return [];
    } finally {
      commit('SET_UI_FLAG', { isFetchingWallet: false });
    }
  },

  addWalletContacts: async ({ commit, dispatch }, { id, contactIds }) => {
    commit('SET_UI_FLAG', { isUpdatingWallet: true });
    try {
      await SurveysAPI.addWalletContacts(id, contactIds);
      await dispatch('fetchWallet', { id });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit('SET_UI_FLAG', { isUpdatingWallet: false });
    }
  },

  removeWalletContacts: async ({ commit, dispatch }, { id, contactIds }) => {
    commit('SET_UI_FLAG', { isUpdatingWallet: true });
    try {
      await SurveysAPI.removeWalletContacts(id, contactIds);
      await dispatch('fetchWallet', { id });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit('SET_UI_FLAG', { isUpdatingWallet: false });
    }
  },

  importFromOwner: async ({ commit, dispatch }, id) => {
    commit('SET_UI_FLAG', { isUpdatingWallet: true });
    try {
      const { data } = await SurveysAPI.importFromOwner(id);
      await dispatch('fetchWallet', { id });
      return data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit('SET_UI_FLAG', { isUpdatingWallet: false });
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, uiFlags) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlags };
  },
  SET_SURVEYS($state, records) {
    Vue.set($state, 'records', records);
  },
  UPSERT_SURVEY($state, record) {
    if (!record) return;
    const index = $state.records.findIndex(item => item.id === record.id);
    if (index === -1) {
      $state.records.push(record);
    } else {
      Vue.set($state.records, index, record);
    }
  },
  DELETE_SURVEY($state, id) {
    Vue.set(
      $state,
      'records',
      $state.records.filter(record => record.id !== Number(id))
    );
  },
  SET_WALLET($state, wallet) {
    Vue.set($state, 'wallet', wallet);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
